import { useEffect, useState, lazy, Suspense, useCallback } from "react";

import { useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { useUser } from "../../../contexts/UserContext/UserContext";

import SettingsSideBar from "./SettingsSideBar";
import SettingsTopTabBar from "./SettingsTopTabBar";
import "./scrollbar-hide.css";

import CompanyInfo from "./companyInfo/CompanyInfo";
import Preferences from "./preferences/Preferences";
import SupportInfo from "./supportInfo/SupportInfo";
import TollUpcharge from "./toll/Toll";
import CouponCode from "./couponCode/CouponCode";

// Lazy load heavy components
const BookingSite = lazy(() => import("./bookingSite/BookingSite"));
const Employees = lazy(() => import("./employee/Employees"));
const Integration = lazy(() => import("./integration/Integration"));
const RentalAgreement = lazy(() => import("./rentalAgreement/RentalAgreement"));
const TaxInfo = lazy(() => import("./taxInfo/TaxInfo"));
const PromoCode = lazy(() => import("./promoCode/PromoCode"));
const SubscriptionBilling = lazy(() => import("./billing/BillingGate"));

// setting component
const CompSettings = () => {
    const { t } = useTranslation("settings");
    const { state, myCompanyData, fetchCompanyData: refreshCompanyData } = useUser();
    const [initialLoading, setInitialLoading] = useState(true);
    const location = useLocation();
    const companyData = myCompanyData || {};
    const [activePage, setActivePage] = useState(
        () => new URLSearchParams(location.search).get("menu") || "companyInfo",
    );

    // Fix infinite re-render issue by moving query logic inside useEffect
    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const menu = query.get("menu");
        // console.log("Menu from URL:", menu);
        if (menu) {
            setActivePage(menu);
        }
    }, [location.search]);

    // `initialLoading` covers first paint only — it starts true and is never raised again.
    //
    // Children render full-page skeletons off this flag, so raising it on every refresh made a
    // *save* replay the *page-load* skeleton: the real rows were swapped for fixed-height
    // placeholders and back again, which is the jump operators see after pressing Save. A refresh
    // already has data on screen, so it should reconcile in place rather than blank the page.
    const fetchCompanyData = useCallback(async () => {
        try {
            await refreshCompanyData();
        } catch (_error) {
            // console.error("Error fetching company data:", error?.response || error);
        } finally {
            setInitialLoading(false);
        }
    }, [refreshCompanyData]);

    useEffect(() => {
        if (!state?.user?.company) return;
        fetchCompanyData();
    }, [fetchCompanyData, state?.user?.company]);

    // Render active component with lazy loading
    const renderActiveComponent = () => {
        const commonProps = {
            initialLoading,
            companyData,
            fetchCompanyData,
        };

        switch (activePage) {
            case "companyInfo":
                return <CompanyInfo {...commonProps} />;

            case "taxInfo":
                return (
                    <Suspense fallback={<div />}>
                        <TaxInfo />
                    </Suspense>
                );

            case "rentalAgreement":
                return (
                    <Suspense fallback={<div />}>
                        <RentalAgreement {...commonProps} />
                    </Suspense>
                );

            case "employee":
                return (
                    <Suspense fallback={<div />}>
                        <Employees />
                    </Suspense>
                );

            case "integration":
                return (
                    <Suspense fallback={<div />}>
                        <Integration {...commonProps} />
                    </Suspense>
                );

            case "bookingSite":
                return (
                    <Suspense fallback={<div />}>
                        <BookingSite initialLoading={initialLoading} companyData={companyData} />
                    </Suspense>
                );

            case "promoCode":
                return (
                    <Suspense fallback={<div />}>
                        <PromoCode initialLoading={initialLoading} companyData={companyData} />
                    </Suspense>
                );

            case "billing":
                return (
                    <Suspense fallback={<div />}>
                        <SubscriptionBilling initialLoading={initialLoading} companyData={companyData} />
                    </Suspense>
                );

            case "userPreferences":
                return (
                    <Suspense fallback={<div />}>
                        <Preferences {...commonProps} />
                    </Suspense>
                );

            case "supportInfo":
                return <SupportInfo initialLoading={initialLoading} companyData={companyData} />;
            case "tollUpcharge":
                return (
                    <Suspense fallback={<div />}>
                        <TollUpcharge
                            initialLoading={initialLoading}
                            companyData={companyData}
                            fetchCompanyData={fetchCompanyData}
                        />
                    </Suspense>
                );
                case "couponCode":
                return (
                    <CouponCode/>
                );

            default:
                return <CompanyInfo {...commonProps} />;
        }
    };

    return (
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:gap-5">
            <div className="flex min-w-0 flex-col gap-3 lg:hidden">
                <h1 className="text-2xl font-bold text-[#2F2F46]">{t("settings")}</h1>
                <SettingsTopTabBar activePage={activePage} />
            </div>
            {/* Pinned just below the sticky GlassPageTopBar (top-4 + h-16 + the layout's gap-3). */}
            <div className="hidden shrink-0 lg:sticky lg:top-[5.75rem] lg:z-10 lg:block">
                <SettingsSideBar activePage={activePage} />
            </div>
            <main className="compSettings min-w-0 flex-1 rounded-2xl bg-white p-4 shadow-[0_1px_3px_rgba(16,16,40,0.06)] ring-1 ring-[#ECECF3] sm:p-6 lg:min-h-[calc(100vh-11rem)] xl:p-8">
                {renderActiveComponent()}
            </main>
        </div>
    );
};

export default CompSettings;
